import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import DropDown, { DropDownContent, DropDownMenuItem } from "../DropDown";
import Item from "./lib/Item";
import KeyNames from "const/KeyNames";
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";

const SPLIT_KEY_NAMES = [KeyNames.ENTER, KeyNames.COMMA];

const TagsInput = (props) => {
  const [stateValue, setStateValue] = useState("");

  const {
    className,
    disabled,
    valid,
    invalid,
    value,
    portal,
    inputValue = stateValue,
    setInputValue = setStateValue,
    autoCompleteData = [],
    onlyAutocompleteData,
    placeholder,
    dropDownClassName,
    groups = [null],
    splitKeys = SPLIT_KEY_NAMES,
    input: InputComponent = "input",
    renderLabel,
    onChange,
    onFocus,
    onBlur
  } = props;

  const { uiTexts } = useSelector(getTextsData);

  const inputRef = useRef();

  const [focused, setFocused] = useState(false);

  const [removingMode, setRemovingMode] = useState(false);

  const [opened, setOpened] = useState(false);

  const notSelectedData = useMemo(() => {
    return autoCompleteData.filter((item) => !value?.includes(item.value));
  }, [autoCompleteData, value]);

  const autoCompleteDataFiltered = useMemo(() => {
    if (!notSelectedData.length) return [];

    const searchString = inputValue.trim().toLocaleLowerCase();

    if (!searchString) return notSelectedData;

    return notSelectedData
      .filter((item) => (item.label || "").toLocaleLowerCase().includes(searchString));
  }, [notSelectedData, inputValue]);

  const completeInput = useCallback(() => {
    setRemovingMode(false);

    const inputValues = inputValue.trim().replace(/\s*,\s*/g, ",").replace(/\s+/g, " ").split(",").filter(Boolean);

    if (inputValues.length && onChange) {
      const uniqueValues = inputValues
        .filter((item) => (!value || !value.includes(item))
          && (!onlyAutocompleteData || autoCompleteData.find(({ label }) => label === item)));

      onChange(
        (Array.isArray(value) ? [...value, ...uniqueValues] : uniqueValues).filter(Boolean)
      );
      setInputValue("");
    }
  }, [autoCompleteData, inputValue, onChange, onlyAutocompleteData, setInputValue, value]);

  const handleContentClick = useCallback(({ target, currentTarget }) => {
    if (target === currentTarget && !disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const onRemove = useCallback((index) => {
    if (onChange && !disabled) {
      setRemovingMode(false);
      onChange(value.filter((tag, idx) => idx !== index));
    }
  }, [disabled, onChange, value]);

  const handleInputChange = useCallback((event) => {
    setRemovingMode(false);
    setInputValue(event.target.value);
  }, [setInputValue]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === KeyNames.BACKSPACE && !inputValue) {
      if (removingMode) {
        onChange(value && value.slice(0, -1));
      } else {
        setRemovingMode(true);
      }
    } else if (splitKeys && splitKeys.includes(event.key)) {
      event.preventDefault();
      completeInput();
    }
  }, [completeInput, onChange, removingMode, splitKeys, value, inputValue]);

  const handleFocus = useCallback((event) => {
    if (onFocus) onFocus(event);
    setOpened(!!autoCompleteDataFiltered.length);
    setFocused(true);
  }, [autoCompleteDataFiltered.length, onFocus]);

  const handleBlur = useCallback((event) => {
    setFocused(false);
    if (onBlur) onBlur(event);
    if (!autoCompleteDataFiltered.length) {
      completeInput();
    }
  }, [autoCompleteDataFiltered.length, completeInput, onBlur]);

  const handleItemClick = useCallback((itemValue) => {
    setInputValue("");
    setRemovingMode(false);
    if (onChange) {
      if (value) {
        onChange(value.includes(itemValue)
          ? value.filter((item) => item !== itemValue)
          : [...value, itemValue]);
      } else {
        onChange([itemValue]);
      }
    }
  }, [onChange, value, setInputValue]);

  const handleCleanClick = useCallback(() => {
    onChange([]);
  }, [onChange]);

  useEffect(() => {
    if (autoCompleteDataFiltered && autoCompleteDataFiltered.includes(inputValue)) completeInput();
  }, [autoCompleteDataFiltered, inputValue, completeInput, setInputValue]);
  useEffect(() => {
    if (!opened && !focused && inputValue.trim().length) {
      completeInput();
    }
  }, [completeInput, inputValue, opened, focused]);

  const IconBefore = invalid ? Icons.WarningCircle : (valid ? Icons.CheckCircle : null);

  return (
    <div
      disabled={disabled}
      className={classNames(
        Css.tagsInput,
        disabled && Css.disabled,
        focused && Css.focused,
        invalid && Css.invalid,
        valid && Css.valid,
        className
      )}>
      <DropDown
        className={Css.dropdown}
        opened={opened}
        setOpened={setOpened}>
        <div className={Css.content} onClick={handleContentClick}>
          {!!IconBefore && <IconBefore className={Css.iconBefore} />}
          {value?.map((tag, index) => (
            <Item
              key={tag.key || tag}
              value={tag}
              index={index}
              highlight={removingMode && index === (value.length - 1)}
              disabled={disabled}
              onRemove={onRemove} />
          ))}
          <div className={Css.input}>
            <InputComponent
              ref={inputRef}
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur} />
            {!!value?.length && (
              <div className={Css.clean} onClick={handleCleanClick}><Icons.X weight="bold" /></div>
            )}
          </div>
        </div>
        {!!autoCompleteDataFiltered?.length && (
          <DropDownContent
            portal={portal}
            className={classNames(
              Css.selectDropdownContent,
              dropDownClassName
            )}>
            <div className={Css.scroll}>
              {!!autoCompleteData.length && (
                autoCompleteDataFiltered.length
                  ? groups.map((group) => {
                    const groupItems = autoCompleteDataFiltered
                      .filter((option) => option && (group ? (option.group === group.value) : !option.group));

                    return !!groupItems.length && (
                      <Fragment key={group ? group.value : 0}>
                        {!!group && (
                          <div className={Css.groupLabel} key={group.value}>{group.label}</div>
                        )}
                        {groupItems.map((option, index) => {
                          const key = String(option.value) + index;

                          return !value?.includes(option.value) && (
                            <DropDownMenuItem
                              key={key}
                              title={option.label}
                              className={Css.option}
                              value={option.value}
                              disabled={option.disabled}
                              onClick={handleItemClick}>
                              <div className={Css.label}>
                                {renderLabel ? renderLabel(option) : option.label}
                              </div>
                            </DropDownMenuItem>
                          );
                        })}
                      </Fragment>
                    );
                  })
                  : <div className={Css.placeholder}>{uiTexts.nothingFound}</div>
              )}
            </div>
          </DropDownContent>
        )}
      </DropDown>
    </div>
  );
};

export default React.memo(TagsInput);
